import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IonButton, IonCheckbox, IonSpinner } from '@ionic/angular/standalone';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { ApiResponse } from '@app/core/models/user.model';
import { Constants } from '@app/core/services/constants';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';

interface RoleOption {
  roleId: string;
  isSystem?: boolean;
  jsonPayload?: {
    name?: string;
    names?: Record<string, string>;
    [key: string]: unknown;
  };
}

interface PaginationInfo {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

interface PagedApiResponse<T> extends ApiResponse<T[]> {
  pagination?: PaginationInfo;
}

@Component({
  selector: 'app-formly-field-prerequisite-roles',
  templateUrl: './formly-field-prerequisite-roles.component.html',
  styleUrls: ['./formly-field-prerequisite-roles.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    IonButton,
    IonCheckbox,
    IonSpinner,
  ],
})
export class FormlyFieldPrerequisiteRolesComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public roleOptions: RoleOption[] = [];
  public isLoading = true;
  public loadError = false;

  private selectedRoleIds = new Set<string>();
  private valueSubscription?: Subscription;

  private static rolesCache: RoleOption[] | null = null;
  private static rolesPromise: Promise<RoleOption[] | null> | null = null;

  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.valueSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.syncSelectionFromValue(value);
      this.changeDetectorRef.markForCheck();
    });

    this.syncSelectionFromValue(this.formControl.value);
    await this.loadRoles();
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
  }

  public get isReadonly(): boolean {
    return Boolean(this.options.formState?.disabled || this.field.props?.disabled || this.field.props?.readonly);
  }

  public trackByRole(index: number, role: RoleOption): string {
    return role.roleId;
  }

  public isRoleSelected(roleId: string): boolean {
    return this.selectedRoleIds.has(roleId);
  }

  public onRoleSelectionChange(roleId: string, checked: boolean): void {
    if (this.isReadonly) {
      return;
    }

    if (checked) {
      this.selectedRoleIds.add(roleId);
    } else {
      this.selectedRoleIds.delete(roleId);
    }

    this.writeValue();
  }

  public getRoleLabel(role: RoleOption): string {
    const names = role.jsonPayload?.names;
    const currentLocale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'fr-FR';
    const languageOnlyLocale = currentLocale.split('-')[0];

    if (names && typeof names === 'object') {
      const localizedName = names[currentLocale]
        || names[languageOnlyLocale]
        || names['fr-FR']
        || names['en-US']
        || Object.values(names).find((value) => typeof value === 'string' && value.trim().length > 0);

      if (typeof localizedName === 'string' && localizedName.trim().length > 0) {
        return localizedName;
      }
    }

    if (typeof role.jsonPayload?.name === 'string' && role.jsonPayload.name.trim().length > 0) {
      return role.jsonPayload.name;
    }

    return role.roleId;
  }

  public async retryLoad(): Promise<void> {
    await this.loadRoles(true);
  }

  private async loadRoles(forceReload = false): Promise<void> {
    this.isLoading = true;
    this.loadError = false;
    this.changeDetectorRef.markForCheck();

    try {
      const roles = await this.getRoles(forceReload);
      this.roleOptions = roles || [];
      this.loadError = !roles;
    } catch (error) {
      console.error('Failed to load prerequisite roles:', error);
      this.roleOptions = [];
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private async getRoles(forceReload: boolean): Promise<RoleOption[] | null> {
    if (!forceReload && FormlyFieldPrerequisiteRolesComponent.rolesCache) {
      return FormlyFieldPrerequisiteRolesComponent.rolesCache;
    }

    if (!forceReload && FormlyFieldPrerequisiteRolesComponent.rolesPromise) {
      return FormlyFieldPrerequisiteRolesComponent.rolesPromise;
    }

    FormlyFieldPrerequisiteRolesComponent.rolesPromise = this.fetchAllRoles(forceReload)
      .then((roles) => {
        FormlyFieldPrerequisiteRolesComponent.rolesCache = roles;
        return roles;
      })
      .finally(() => {
        FormlyFieldPrerequisiteRolesComponent.rolesPromise = null;
      });

    return FormlyFieldPrerequisiteRolesComponent.rolesPromise;
  }

  private async fetchAllRoles(forceReload: boolean): Promise<RoleOption[]> {
    const collectedRoles: RoleOption[] = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
      const response = await firstValueFrom(this.http.get<PagedApiResponse<RoleOption>>(`${environment.apiUrl}/roles`, {
        params: {
          page: currentPage.toString(),
          pageSize: Constants.maxPageSize.toString(),
          isActive: 'true',
          ...(forceReload ? { _: `${Date.now()}-${currentPage}` } : {}),
        },
      }));

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch roles');
      }

      collectedRoles.push(...(Array.isArray(response.data) ? response.data : []));
      totalPages = Math.max(response.pagination?.totalPages || 1, 1);
      currentPage += 1;
    } while (currentPage <= totalPages);

    return collectedRoles.sort((left, right) => left.roleId.localeCompare(right.roleId));
  }

  private syncSelectionFromValue(value: unknown): void {
    const selectedRoles = Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      : [];

    this.selectedRoleIds = new Set(selectedRoles);
  }

  private writeValue(): void {
    const nextValue = Array.from(this.selectedRoleIds).sort((left, right) => left.localeCompare(right));
    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.onChange(nextValue);
    this.changeDetectorRef.markForCheck();
  }
}